import { useMemo } from 'react';

import {
  useAllZoomProcessingLogQuery,
  ZoomProcessingLogFieldsFragment,
} from 'core/graphql';

interface IProcessingLogOptions {
  level?: string;
  step?: string;
  /** Poll interval in ms — pass `0` to disable polling. */
  pollInterval?: number;
}

interface IProcessingLogHook {
  entries: ZoomProcessingLogFieldsFragment[];
  totalCount: number;
  loading: boolean;
  error?: string;
  refetch: () => void;
}

/**
 * Processing journal of one lesson — backs the «Processing log» timeline on the
 * lesson card and the full-log modal. See docs (ZoomProcessingLog).
 */
export const useProcessingLog = (
  lessonId?: string,
  { level, step, pollInterval = 0 }: IProcessingLogOptions = {},
): IProcessingLogHook => {
  const { data, loading, error, refetch } = useAllZoomProcessingLogQuery({
    variables: {
      lessonId: lessonId ?? '',
      level: level || null,
      step: step || null,
      first: 200,
    },
    skip: !lessonId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    pollInterval,
  });

  const entries = useMemo(
    () =>
      (data?.allZoomProcessingLog?.edges ?? [])
        .map((edge) => edge?.node)
        .filter((node): node is ZoomProcessingLogFieldsFragment =>
          Boolean(node),
        ),
    [data],
  );

  return {
    entries,
    totalCount: data?.allZoomProcessingLog?.totalCount ?? 0,
    loading,
    error: error?.message,
    refetch: () => {
      refetch();
    },
  };
};
